/**
* DropDownComponent.js
* 
* Functionality of a drop down box that allows you to select a value.
* 
* Created on 27-aug-2014, 12:23:48
*
* @author Ivo Swartjes for Ranj
**/

import Button from "../buttons/Button";

export default class DropDownComponent {
    /**
     * 
     * @param {DisplayObject} skin 
     * @param {MovieClip} itemButtonClass 
     * @param {Array<{label: string}>} data 
     * @param {string} defaultText 
     * @param {boolean} expandUp 
     * @param {{verticallyCenterText: boolean}} styleOptions 
     */
    constructor(skin, itemButtonClass, data = [], defaultText = null, expandUp = false, styleOptions = {verticallyCenterText: false}) {
        this._skin = skin;
        this._itemButtonClass = itemButtonClass;
        this._expandUp = expandUp;
        this._styleOptions = styleOptions;

        // state variables
        this._itemButtons = [];
        this.selectedItem = null;
        this._isOpen = false;

        // skin elements
        if(!this._skin.itemContainer){
            console.warn("[DropDownComponent] Missing 'itemContainer' child in skin", this._skin);
        }
        if(!this._skin.dropDownButton){
            console.warn("[DropDownComponent] Missing 'dropDownButton' child in skin", this._skin);
        }

        this._itemContainer = this._skin.itemContainer;
        this._dropDownBackground = this._skin.dropDownBackground; // optional
        this._indicator = this._skin.dropDownIndicator; // optional

        // event listeners
        this._handleDropDownButtonClickedBound = this._handleDropDownButtonClicked.bind(this);
        this._handleItemButtonClickedBound = this._handleItemButtonClicked.bind(this);       
        
        this._dropdownButton = new Button(this._skin.dropDownButton);
        this._dropdownButton.setLabel(defaultText);
        this._dropdownButton.addEventListener("click", this._handleDropDownButtonClickedBound);
        
        this.setData(data);
        this.closeDropDown();
    }

    destroy() {
        this._dropdownButton.removeEventListener("click", this._handleDropDownButtonClickedBound);
        this._dropdownButton.destroy();

        for(let itemButton of this._itemButtons) {
            itemButton.destroy();
        }
    }

    /**
     * Sets data for the DropDown by creating the buttons to populate the options list.
     * 
     * NB: it resets the current buttons to create a new set
     * 
     * @param {Array<{label: string}>} data 
     */
    setData(data) {
        this._data = data;
        
        for (var i = 0; i < this._itemButtons.length; i++) {
            var itemButton = this._itemButtons[i];
            this._itemContainer.removeChild(itemButton.skin);
            itemButton.destroy();
        }
        
        this._itemButtons = [];
        
        var totalHeight = this._expandUp ? -this._dropdownButton.skin.nominalBounds.height * 2 : 0;
        for (var i = 0; i < data.length; i++) {
            var itemButtonSkin = new this._itemButtonClass();
            var itemButton = new Button(itemButtonSkin);
            itemButton.item = data[i];

            if (this._styleOptions.verticallyCenterText) {
                itemButton.setCenteredLabel(itemButton.item.label);
            } else {
                itemButton.setLabel(itemButton.item.label);
            }
            
            itemButton.addEventListener("click", this._handleItemButtonClickedBound);
            this._itemButtons.push(itemButton);
               
            if (this._expandUp) {
                itemButton.skin.y += totalHeight;
                totalHeight -= itemButton.skin.nominalBounds.height;
            } else {
                itemButton.skin.y += totalHeight;
                totalHeight += itemButton.skin.nominalBounds.height;
            }
            this._itemContainer.addChild(itemButton.skin);
        }
    }

    selectItem(item) {
        if (this._styleOptions.verticallyCenterText) {
            this._dropdownButton.setCenteredLabel(item.label);
        } else {
            this._dropdownButton.setLabel(item.label);
        }
                
        this.selectedItem = item;
        
        this.closeDropDown();
        
        this.dispatchEvent("change");        
    }

    openDropDown() {
        this._isOpen = true;
        
        if (this._dropDownBackground) {
            this._dropDownBackground.visible = true;
        }

        if (this._indicator) {
            this._indicator.rotation = this._expandUp ? 180 : 0;
        }

        for (var i = 0; i < this._itemButtons.length; i++) {
            this._itemButtons[i].skin.visible = true;
        }
    }

    closeDropDown() {        
        this._isOpen = false;

        if (this._dropDownBackground) {
            this._dropDownBackground.visible = false;
        }
        
        if (this._indicator) {
            this._indicator.rotation = this._expandUp ? 0 : 180;
        }

        for (var i = 0; i < this._itemButtons.length; i++) {
            this._itemButtons[i].skin.visible = false;
        }
    }

    _handleDropDownButtonClicked(e) {
        e.stopImmediatePropagation();        
        this._isOpen = !this._isOpen;
        if (this._isOpen) {
            // Open            
            this.openDropDown();
        } else {
            // Close
            this.closeDropDown();
        }        
        this.dispatchEvent("click");
    }

    _handleItemButtonClicked(e) {        
        e.stopImmediatePropagation();        
        this.selectItem(e.target.item);
        this.dispatchEvent("click");
    }
}

createjs.EventDispatcher.initialize(DropDownComponent.prototype);